import { useState, useEffect, useMemo } from 'react';
import { useProjectNeeds, useUpsertNeeds, useProjectAssignments, useProjectOverview, useCreateAssignment, useDeleteAssignment, useAvailablePeople, useShiftProjectPlan, useFillCapacityNeed } from '../../hooks/useCapacity';
import { useProjectRoles } from '../../hooks/useLeads';
import { useProject } from '../../hooks/useProjects';
import { useFlag } from '../../services/flagsContext';
import { formatFte, parseLocaleNumber } from '../../utils/format';
import { planCapacityAssignments } from '../../utils/capacityAssignment';
import { formatAllocation, formatAllocationHours } from './allocationFormat';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Spinner } from '../common/Spinner';
import type { AssignmentStatus, AvailablePerson } from '../../services/api/capacity';

interface ProjectCapacityGridProps {
  projectId: number;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function generateMonths(count: number, now: Date = new Date()): string[] {
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return result;
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m!, 10) - 1]} ${year!.slice(2)}`;
}

function personName(p: { firstName: string | null; lastName: string | null }): string {
  return [p.lastName, p.firstName].filter(Boolean).join(' ') || '?';
}

// KYI-CAP-02: Derive a YYYY-MM month key from an ISO date string by taking
// the first 7 characters. Matches the month-key format used throughout the
// grid so string >= / <= comparison is valid.
function toMonthKey(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 7) : null;
}

/**
 * MKS-535 — the two layouts of a month cell, picked by
 * `mks_535_planner_cell_fit`. The twin of `PlanningMatrix`'s `CELL_LAYOUT`, for
 * the same reason: a cell with several people on one role in one month lays
 * their names out on centred flex rows that cannot shrink, so in a 120 px
 * `table-layout: fixed` column "Horáčková Kateřina 0.50 x" runs straight over
 * the next month.
 *
 * `fit` clips the cell, bounds the list's height, and lets a name wrap inside
 * the column instead of pushing past it — the full "name — FTE" stays in the
 * row's `title`. `w-max min-w-full` keeps the columns at their set widths on a
 * narrow screen, where a fixed-layout table with an auto width would otherwise
 * squeeze them below it and start the overlap.
 */
const GRID_LAYOUT = {
  fit: {
    table: 'w-max min-w-full divide-y divide-gray-200 text-xs',
    monthCell: 'px-2 py-1 text-center align-top overflow-hidden',
    assignmentList: 'mt-0.5 space-y-0.5 max-h-24 overflow-y-auto',
    assignment: 'flex items-start justify-center gap-0.5 text-[10px] text-gray-500',
    person: 'min-w-0 break-words',
    remove: 'shrink-0 text-gray-900 hover:text-red-700',
  },
  legacy: {
    table: 'divide-y divide-gray-200 text-xs',
    monthCell: 'px-2 py-1 text-center',
    assignmentList: 'mt-0.5 space-y-0.5',
    assignment: 'flex items-center justify-center gap-0.5 text-[10px] text-gray-500',
    person: '',
    remove: 'text-gray-900 hover:text-red-700',
  },
} as const;

// Need-cell input with local state. The parent fires upsertNeeds (which uses
// fte=0 to mean "delete the row") only on blur — committing on every keystroke
// caused two bugs together: (1) typing `0,01` on a Czech keyboard parses as
// `parseFloat("0,01") = 0` → DELETE, then a follow-up keystroke sometimes
// re-creates the row with the wrong number; (2) intermediate states like `0,`
// or empty string also fired DELETE mutations. Local state lets the user type
// freely; commit happens once on blur or Enter.
function NeedCellInput({
  value,
  onCommit,
  disabled,
}: {
  value: number;
  onCommit: (fte: number) => void;
  disabled?: boolean;
}) {
  const [local, setLocal] = useState<string>(value > 0 ? String(value) : '');
  useEffect(() => {
    setLocal(value > 0 ? String(value) : '');
  }, [value]);

  const commit = () => {
    const fte = parseLocaleNumber(local);
    if (fte !== value) onCommit(fte);
  };

  return (
    <input
      type="number"
      min={0}
      max={99.99}
      step={0.01}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      disabled={disabled}
      title="Value between 0.01 and 99.99 (e.g. 0.5, 1.5, 2.25)"
      className="w-14 rounded border border-gray-200 px-1 py-0.5 text-center text-xs focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
      placeholder="0"
    />
  );
}

export default function ProjectCapacityGrid({ projectId }: ProjectCapacityGridProps) {
  const nowYear = new Date().getUTCFullYear();
  const nowMonth = new Date().getUTCMonth();
  // nowYear/nowMonth in deps ensures the memo recomputes on month/year boundary
  // (fixes F-2203: useMemo([]) captures stale "now").
  const months = useMemo(() => generateMonths(12, new Date(nowYear, nowMonth)), [nowYear, nowMonth]);
  const { data: project } = useProject(projectId);
  const { data: needsData, isLoading: needsLoading } = useProjectNeeds(projectId);
  const { data: assignmentsData } = useProjectAssignments(projectId);
  const { data: overviewData } = useProjectOverview(projectId);
  const { data: rolesData } = useProjectRoles();
  const upsertNeeds = useUpsertNeeds();
  const createAssignment = useCreateAssignment();
  const deleteAssignmentMutation = useDeleteAssignment();
  // MKS-358 — the gap-fill write. With the flag on, "+x needed" puts the
  // person on a PROJECT assignment (the canonical staffing model) instead of
  // minting a capacity-assignment row that grants nothing.
  const allocationUi = useFlag('mks_358_planner_allocation_ui');
  // MKS-535 — the month cell that keeps several assigned people inside its
  // own column. See GRID_LAYOUT.
  const cellFit = useFlag('mks_535_planner_cell_fit');
  const layout = cellFit ? GRID_LAYOUT.fit : GRID_LAYOUT.legacy;
  const fillNeed = useFillCapacityNeed();

  const [addingRole, setAddingRole] = useState<{ roleId: number; month: string } | null>(null);
  const [showAllPeople, setShowAllPeople] = useState(false);
  const [assignSubsequent, setAssignSubsequent] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState<{ id: number; name: string; remainingFte: number } | null>(null);
  const [assignFte, setAssignFte] = useState('');
  // The confirmed/tentative choice the gap-fill posts. Defaults to
  // `tentative`: the planner is where work is penciled in, and a tentative
  // assignment grants no project access until somebody confirms it, so the
  // safe default is the one that cannot hand out access by accident.
  const [assignStatus, setAssignStatus] = useState<AssignmentStatus>('tentative');
  const [deletingRole, setDeletingRole] = useState<{ roleId: number; roleName: string } | null>(null);
  // F16-004 — Fill row modal state (replaces window.prompt/alert)
  const [fillRowState, setFillRowState] = useState<{ roleId: number; roleName: string } | null>(null);
  const [fillRowValue, setFillRowValue] = useState('');
  const [fillRowError, setFillRowError] = useState<string | null>(null);
  const { data: availableData } = useAvailablePeople(
    addingRole?.roleId ?? null,
    addingRole?.month ?? null,
    showAllPeople
  );

  const shiftPlan = useShiftProjectPlan();

  const needs = useMemo(() => needsData?.data ?? [], [needsData]);
  const assignments = assignmentsData?.data ?? [];
  const overview = overviewData?.data ?? [];
  const allRoles = rolesData?.data ?? [];
  const currentMonth = getCurrentMonth();

  // KYI-CAP-02: clamp bulk-write paths to the project's date range.
  // A null start/end means unbounded on that side — do not clamp.
  const startMonth = toMonthKey(project?.startDate);
  const endMonth = toMonthKey(project?.endDate);
  const withinProjectRange = (m: string) =>
    (!startMonth || m >= startMonth) && (!endMonth || m <= endMonth);

  // Get unique role IDs used in needs
  const usedRoleIds = useMemo(() => {
    const ids = new Set(needs.map((n) => n.roleId));
    return [...ids];
  }, [needs]);

  const usedRoles = allRoles.filter((r) => usedRoleIds.includes(r.id));
  const unusedRoles = allRoles.filter((r) => !usedRoleIds.includes(r.id));

  const handleAddRole = (roleId: number) => {
    // Add with 0 FTE for first month to show the row
    upsertNeeds.mutate({ projectId, needs: [{ roleId, month: months[0]!, fte: 1 }] });
  };

  // F16-004 — Open the Fill-row modal (replaces window.prompt). Validation
  // logic preserved verbatim from the previous prompt/alert flow:
  //   * empty input → no-op
  //   * non-numeric → "Enter a number between 0 and 99.99 (e.g. 0.5, 1.5, 2.25)"
  //   * out of [0, 99.99] → "Enter a value between 0 and 99.99"
  // (See Petra UAT 2026-05-20 / review WR-02 — these messages are the spec.)
  const openFillRow = (roleId: number, roleName: string) => {
    setFillRowState({ roleId, roleName });
    setFillRowValue('');
    setFillRowError(null);
  };

  const submitFillRow = () => {
    if (!fillRowState) return;
    const trimmed = fillRowValue.trim();
    if (trimmed === '') {
      setFillRowState(null);
      return;
    }
    if (!/^[0-9]+([.,][0-9]{1,2})?$/.test(trimmed)) {
      setFillRowError('Enter a number between 0 and 99.99 (e.g. 0.5, 1.5, 2.25)');
      return;
    }
    const fte = parseLocaleNumber(trimmed);
    if (!isFinite(fte) || fte < 0 || fte > 99.99) {
      setFillRowError('Enter a value between 0 and 99.99');
      return;
    }
    const targets = months
      .filter((m) => m >= currentMonth && withinProjectRange(m))
      .map((month) => ({ roleId: fillRowState.roleId, month, fte }));
    if (targets.length > 0) {
      upsertNeeds.mutate({ projectId, needs: targets });
    }
    setFillRowState(null);
  };

  /**
   * MKS-358 — the gap-fill write behind `mks_358_planner_allocation_ui`.
   *
   * A project assignment is a date RANGE, not a row per month, so covering
   * several months is one assignment stretched over them: posting the first
   * target month creates (or extends) it, posting the last widens its end
   * date, and every month between is covered by that range. Sequential, so
   * the second post sees the first's assignment and extends it instead of
   * racing it into a duplicate.
   */
  const submitGapFill = async (roleId: number, startMonth: string, userId: number, share: number) => {
    const targetMonths = assignSubsequent
      ? months.filter(
          (m) =>
            m >= startMonth &&
            withinProjectRange(m) &&
            needs.some((n) => n.roleId === roleId && n.month === m),
        )
      : [startMonth];
    const posts = [startMonth, ...targetMonths.slice(-1).filter((m) => m !== startMonth)];

    for (const month of posts) {
      await fillNeed.mutateAsync({
        projectId,
        roleId,
        month,
        userId,
        status: assignStatus,
        allocation: { share },
      });
    }
  };

  if (needsLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => shiftPlan.mutate({ projectId, direction: -1 })}
            isLoading={shiftPlan.isPending}
            title="Shift entire plan 1 month earlier"
          >
            &larr; 1 month
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => shiftPlan.mutate({ projectId, direction: 1 })}
            isLoading={shiftPlan.isPending}
            title="Shift entire plan 1 month later"
          >
            1 month &rarr;
          </Button>
        </div>
      </div>

      <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className={layout.table} style={{ tableLayout: 'fixed' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-500 uppercase w-[310px]">Role</th>
              {months.map((m) => (
                <th key={m} className="px-2 py-2 text-center font-medium text-gray-500 uppercase whitespace-nowrap w-[120px]">
                  {monthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {usedRoles.map((role) => {
              const roleNeeds = needs.filter((n) => n.roleId === role.id);
              const roleAssignments = assignments.filter((a) => a.roleId === role.id);

              const roleLabel = role.code ? `${role.code} — ${role.name}` : role.name;
              return (
                <tr key={role.id}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        {/* CP-FIX-01 — delete-whole-role cross (LEFT) with confirm modal */}
                        <button
                          onClick={() => setDeletingRole({ roleId: role.id, roleName: roleLabel })}
                          className="text-gray-400 hover:text-red-600 text-sm leading-none"
                          aria-label={`Delete role ${roleLabel} from this project`}
                          title="Delete this role from the project"
                        >
                          &times;
                        </button>
                        <span>{roleLabel}</span>
                      </div>
                      <button
                        onClick={() => openFillRow(role.id, role.name)}
                        className="text-[10px] text-gray-400 hover:text-primary-600"
                        title="Apply an FTE value to all future months for this role"
                      >
                        Fill row &rarr;
                      </button>
                    </div>
                  </td>
                  {months.map((m) => {
                    const need = roleNeeds.find((n) => n.month === m);
                    const monthAssignments = roleAssignments.filter((a) => a.month === m);
                    const needed = need?.fte ?? 0;
                    // MKS-358 — with the flag on, cover is read from the
                    // project overview's role row, which counts the project
                    // assignments the gap-fill writes (and splits them by
                    // status). Flag off: the capacity-assignment rows under
                    // the cell, as today.
                    const ovRole = allocationUi
                      ? overview.find((o) => o.month === m)?.roles.find((r) => r.roleId === role.id)
                      : undefined;
                    const assigned = ovRole
                      ? ovRole.assigned
                      : monthAssignments.reduce((s, a) => s + a.fte, 0);
                    const gap = Math.round((needed - assigned) * 100) / 100;
                    const isPast = m < currentMonth;

                    return (
                      <td key={m} className={`${layout.monthCell} ${isPast ? 'bg-gray-50' : ''}`}>
                        {/* Need input — local state, commits on blur (Czech-comma-safe). */}
                        <NeedCellInput
                          value={needed}
                          onCommit={(fte) =>
                            upsertNeeds.mutate({ projectId, needs: [{ roleId: role.id, month: m, fte }] })
                          }
                          disabled={isPast}
                        />
                        {/* Assigned people */}
                        {monthAssignments.length > 0 && (
                          <div className={layout.assignmentList}>
                            {monthAssignments.map((a) => (
                              <div
                                key={a.id}
                                className={layout.assignment}
                                // The whole name, for when wrapping still has
                                // to break a long one. Off the flag the line
                                // never wraps and carries no tooltip today.
                                title={cellFit ? `${personName(a.user)} — ${formatFte(a.fte)}` : undefined}
                              >
                                <span className={`${layout.person} ${a.isRoleMismatch ? 'text-amber-600' : ''}`.trim()}>{personName(a.user)} {formatFte(a.fte)}</span>
                                {!isPast && (
                                  <button
                                    onClick={() => deleteAssignmentMutation.mutate(a.id)}
                                    className={layout.remove}
                                    aria-label={`Remove ${personName(a.user)} from this assignment`}
                                  >
                                    x
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* MKS-358 — how much of the cover is only penciled
                            in. Rendered only when there IS something
                            tentative, so a fully booked cell stays as short
                            as it is today. */}
                        {ovRole && ovRole.tentative > 0 && (
                          <div className="mt-0.5 space-y-0.5 text-[10px] leading-tight text-gray-500">
                            <div title="Confirmed cover">{formatFte(ovRole.confirmed)} confirmed</div>
                            <div
                              className="rounded border border-dashed border-current px-0.5"
                              title="Tentative cover — planned, not confirmed"
                            >
                              {formatFte(ovRole.tentative)} tentative
                            </div>
                          </div>
                        )}
                        {/* Gap indicator + add button */}
                        {needed > 0 && gap > 0 && !isPast && (
                          <button
                            onClick={() => { setAddingRole({ roleId: role.id, month: m }); setShowAllPeople(false); }}
                            className="mt-0.5 text-[10px] text-red-600 hover:text-red-800 font-medium"
                          >
                            &#9888; +{formatFte(gap)} needed
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200">
            <tr>
              <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500">Status</td>
              {months.map((m) => {
                const ov = overview.find((o) => o.month === m);
                const needed = ov?.totalNeeded ?? 0;
                const assigned = ov?.totalAssigned ?? 0;
                const isCovered = needed > 0 && assigned >= needed;
                const hasGap = needed > 0 && assigned < needed;
                return (
                  <td key={m} className="px-2 py-2 text-center">
                    {isCovered && <span className="text-green-600" title="Fully covered">&#10003;</span>}
                    {hasGap && <span className="text-red-500" title={`Gap: ${formatFte(needed - assigned)} FTE`}>&#9888;</span>}
                    {needed === 0 && <span className="text-gray-300">-</span>}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Add role row */}
      {unusedRoles.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <select
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) handleAddRole(parseInt(e.target.value, 10));
              e.target.value = '';
            }}
            aria-label="Add a role to project capacity"
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">Add a role...</option>
            {unusedRoles.map((r) => (
              <option key={r.id} value={r.id}>{r.code ? `${r.code} — ${r.name}` : r.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* People picker modal */}
      {addingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="capacity-assign-modal-title" onClick={() => { setAddingRole(null); setSelectedPerson(null); }}>
          <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 id="capacity-assign-modal-title" className="text-sm font-semibold text-gray-900 mb-2">
              Assign person — {allRoles.find((r) => r.id === addingRole.roleId)?.name} in {monthLabel(addingRole.month)}
            </h3>
            <div className="space-y-1 mb-2">
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input type="checkbox" checked={assignSubsequent} onChange={(e) => setAssignSubsequent(e.target.checked)} className="rounded border-gray-300" />
                Also assign to all subsequent months with same role need
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input type="checkbox" checked={showAllPeople} onChange={(e) => setShowAllPeople(e.target.checked)} className="rounded border-gray-300" />
                Show all people (override role filter)
              </label>
            </div>

            {selectedPerson ? (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedPerson.name}</span>
                  <span className="text-gray-400 ml-2">({formatFte(selectedPerson.remainingFte)} avail)</span>
                </div>
                <div>
                  <label htmlFor="capacity-assign-amount" className="block text-xs font-medium text-gray-600 mb-1">
                    {allocationUi ? '% of capacity to allocate' : 'FTE to assign'}
                  </label>
                  <input
                    id="capacity-assign-amount"
                    type="number"
                    min={allocationUi ? 1 : 0.01}
                    max={allocationUi ? 100 : 99.99}
                    step={allocationUi ? 1 : 0.01}
                    value={assignFte}
                    onChange={(e) => setAssignFte(e.target.value)}
                    title={
                      allocationUi
                        ? 'Share of this person’s own capacity, 1 to 100 (e.g. 60 for 60 %)'
                        : 'Value between 0.01 and 99.99 (e.g. 0.5, 1.5, 2.25)'
                    }
                    className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>
                {/* MKS-358 — confirmed or only penciled in. A tentative
                    assignment grants no project access until it is confirmed,
                    so the choice is made here rather than assumed. */}
                {allocationUi && (
                  <fieldset>
                    <legend className="block text-xs font-medium text-gray-600 mb-1">Status</legend>
                    <div className="flex items-center gap-4">
                      {(['confirmed', 'tentative'] as const).map((status) => (
                        <label key={status} className="flex items-center gap-1.5 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="capacity-assign-status"
                            value={status}
                            checked={assignStatus === status}
                            onChange={() => setAssignStatus(status)}
                            className="border-gray-300"
                          />
                          {status === 'confirmed' ? 'Confirmed' : 'Tentative'}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setSelectedPerson(null)}>Back</Button>
                  <Button size="sm" disabled={!assignFte || parseLocaleNumber(assignFte) <= 0} onClick={() => {
                    const typed = parseLocaleNumber(assignFte);

                    if (allocationUi) {
                      // The input is a per cent of the person; the endpoint
                      // takes a share in 0..1.
                      void submitGapFill(
                        addingRole.roleId,
                        addingRole.month,
                        selectedPerson.id,
                        typed / 100,
                      );
                      setAddingRole(null);
                      setSelectedPerson(null);
                      return;
                    }

                    // MKS-229: each month takes its own remaining need, capped
                    // at the typed FTE — not the first month's value for all.
                    const planned = planCapacityAssignments({
                      roleId: addingRole.roleId,
                      userId: selectedPerson.id,
                      startMonth: addingRole.month,
                      fte: typed,
                      months,
                      needs,
                      assignments,
                      assignSubsequent,
                      isMonthAllowed: withinProjectRange,
                    });

                    for (const { month, fte } of planned) {
                      createAssignment.mutate({ projectId, userId: selectedPerson.id, roleId: addingRole.roleId, month, fte });
                    }
                    setAddingRole(null);
                    setSelectedPerson(null);
                  }}>Assign</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
                  {(availableData?.data ?? []).map((person: AvailablePerson) => {
                    const roleNeeds = needs.filter((n) => n.roleId === addingRole.roleId && n.month === addingRole.month);
                    const roleAssigned = assignments.filter((a) => a.roleId === addingRole.roleId && a.month === addingRole.month).reduce((s, a) => s + a.fte, 0);
                    const gap = Math.max(0, (roleNeeds[0]?.fte ?? 1) - roleAssigned);
                    const defaultFte = Math.round(Math.min(gap || 1, person.remainingFte > 0 ? person.remainingFte : 1) * 100) / 100;
                    // MKS-358 — the input is a per cent of the person, so the
                    // default is what is left of them, not an FTE.
                    // MKS-535 — with `mks_535_percent_of_available` on,
                    // `allocatedPercent` is a per cent of the AVAILABLE hours,
                    // so `100 - allocatedPercent` is the same share of the same
                    // capacity that `freeHours` is counting below. Off the flag
                    // the two still differ for anyone with time off, exactly as
                    // they do today.
                    const freeHours = Math.max(0, Math.round((person.availableHours - person.allocatedHours) * 100) / 100);
                    const defaultPercent = Math.max(1, Math.round(100 - person.allocatedPercent));
                    return (
                      <button
                        key={person.id}
                        onClick={() => {
                          setSelectedPerson({ id: person.id, name: personName(person), remainingFte: person.remainingFte });
                          setAssignFte(String(allocationUi ? defaultPercent : defaultFte));
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex justify-between items-center ${!person.hasRoleTag ? 'text-amber-600' : 'text-gray-700'}`}
                        title={`${person.workingDays} working days${person.timeOffDays > 0 ? `, ${person.timeOffDays} day(s) time off` : ''} → ${formatFte(person.availableFte)} max FTE\nAllocated: ${formatFte(person.allocatedFte)}, Remaining: ${formatFte(person.remainingFte)}${allocationUi ? `\nFree: ${formatAllocationHours(freeHours)} of ${formatAllocationHours(person.availableHours)}` : ''}`}
                      >
                        <div>
                          <span>{personName(person)}</span>
                          {!person.hasRoleTag && <span className="text-[10px] ml-1">(no role tag)</span>}
                          {person.timeOffDays > 0 && (
                            <span className="ml-1 text-[10px] text-amber-500">({person.timeOffDays}d off)</span>
                          )}
                        </div>
                        <span className={`text-xs shrink-0 ${person.remainingFte <= 0 ? 'text-red-500' : 'text-gray-400'}`}>
                          {allocationUi
                            ? `${formatAllocation(person.allocatedPercent, person.allocatedHours)} booked`
                            : `${formatFte(person.remainingFte)} avail`}
                        </span>
                      </button>
                    );
                  })}
                  {(availableData?.data ?? []).length === 0 && (
                    <p className="px-3 py-4 text-sm text-gray-400 text-center">No people available</p>
                  )}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button variant="secondary" size="sm" onClick={() => { setAddingRole(null); setSelectedPerson(null); }}>Close</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* F16-004 — Fill-row modal (replaces window.prompt/alert). Same
          validation rules as before; errors now render inline under the
          input instead of blocking the tab with a native dialog. */}
      {fillRowState && (
        <Modal
          isOpen={true}
          onClose={() => setFillRowState(null)}
          title={`Fill row — ${fillRowState.roleName}`}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setFillRowState(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitFillRow}>
                Apply
              </Button>
            </div>
          }
        >
          <p className="text-sm text-gray-600 mb-3">
            FTE to apply to all future months for &ldquo;{fillRowState.roleName}&rdquo;? Use 0 to clear.
          </p>
          <input
            type="text"
            inputMode="decimal"
            value={fillRowValue}
            onChange={(e) => {
              setFillRowValue(e.target.value);
              if (fillRowError) setFillRowError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitFillRow();
            }}
            autoFocus
            placeholder="e.g. 0.5"
            className={`w-32 rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              fillRowError ? 'border-red-500' : 'border-gray-300'
            }`}
            aria-invalid={fillRowError ? 'true' : 'false'}
            aria-describedby={fillRowError ? 'fill-row-error' : undefined}
          />
          {fillRowError && (
            <p id="fill-row-error" className="mt-2 text-xs text-red-600">
              {fillRowError}
            </p>
          )}
        </Modal>
      )}

      {/* CP-FIX-01 — delete-role confirm modal. Cascades fte=0 across every
          month (existing fte=0 = DELETE invariant per NeedCellInput comment
          above) + deletes every assignment tied to that role on this project. */}
      {deletingRole && (() => {
        const roleNeeds = needs.filter((n) => n.roleId === deletingRole.roleId);
        const roleAssignments = assignments.filter((a) => a.roleId === deletingRole.roleId);
        const monthEntryCount = roleNeeds.filter((n) => n.fte > 0).length;
        const assignmentCount = roleAssignments.length;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="capacity-delete-role-modal-title" onClick={() => setDeletingRole(null)}>
            <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 id="capacity-delete-role-modal-title" className="text-sm font-semibold text-gray-900 mb-2">Delete role {deletingRole.roleName}?</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will remove the role plus {monthEntryCount} monthly FTE entries and {assignmentCount} assigned people.
                This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setDeletingRole(null)}>Cancel</Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    if (roleNeeds.length > 0) {
                      upsertNeeds.mutate({
                        projectId,
                        needs: roleNeeds.map((n) => ({ roleId: n.roleId, month: n.month, fte: 0 })),
                      });
                    }
                    for (const a of roleAssignments) {
                      deleteAssignmentMutation.mutate(a.id);
                    }
                    setDeletingRole(null);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
